"use client";

import { c as _c } from "react-compiler-runtime";
import { AlertCircle } from "lucide-react";
import { useRef, useState } from "react";
import Glasses from "@/assets/icons/glasses.svg";
import Sun from "@/assets/icons/sun.svg";
import User from "@/assets/icons/user.svg";
import { Text } from "@/components/primitives";
import { type Dictionary, useTranslation } from "@/core/i18n";
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/heic", "image/heif", "image/webp"];
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const TIP_ICON_CLASS = "h-5 w-5 text-primary [&_*]:stroke-[1.5] [&_*]:[stroke-linecap:round] [&_*]:[stroke-linejoin:round]";
export const FACE_OUTLINE_FRAME_CLASS = "relative aspect-[13/18] w-[min(68vw,12rem)] max-h-[min(36dvh,16rem)] shrink-0 sm:h-72 sm:w-52 sm:max-h-none";

/**
 * Every string and the framing outline the uploader draws. Defaults reproduce the aesthetics
 * wording and face oval exactly, so callers that pass nothing are unaffected; the lash/brow flow
 * supplies its own set because a full-face oval and "keep your face clear" hints are wrong
 * guidance when the client is being asked for a close-up of their eyes.
 */
export type PhotoUploaderCopy = {
  readonly dropPhoto: keyof Dictionary;
  readonly tapToUpload: keyof Dictionary;
  readonly tapToUploadMobile: keyof Dictionary;
  readonly supportedFormats: keyof Dictionary;
  readonly supportedFormatsMobile: keyof Dictionary;
  readonly unsupportedFileType: keyof Dictionary;
  readonly fileTooLarge: keyof Dictionary;
  readonly tryDifferentPhoto: keyof Dictionary;
  readonly bestResults: keyof Dictionary;
  readonly hints: readonly [keyof Dictionary, keyof Dictionary, keyof Dictionary];
};
export const AESTHETICS_PHOTO_UPLOADER_COPY: PhotoUploaderCopy = {
  dropPhoto: "assessmentSteps.studioSession.photoUpload.dropPhoto",
  tapToUpload: "assessmentSteps.studioSession.photoUpload.tapToUploadFront",
  tapToUploadMobile: "assessmentSteps.studioSession.photoUpload.tapToUploadFrontMobile",
  supportedFormats: "assessmentSteps.studioSession.photoUpload.supportedFormats",
  supportedFormatsMobile: "assessmentSteps.studioSession.photoUpload.supportedFormatsMobile",
  unsupportedFileType: "assessmentSteps.studioSession.photoUpload.unsupportedFileType",
  fileTooLarge: "assessmentSteps.studioSession.photoUpload.fileTooLarge",
  tryDifferentPhoto: "assessmentSteps.studioSession.photoUpload.tryDifferentPhoto",
  bestResults: "assessmentSteps.studioSession.tips.bestResults",
  hints: ["assessmentSteps.studioSession.photoUpload.hintFaceClear", "assessmentSteps.studioSession.photoUpload.hintLighting", "assessmentSteps.studioSession.photoUpload.hintAccessories"]
};
type AestheticsPhotoUploaderProps = {
  readonly disabled?: boolean;
  readonly errorMessage?: string | null;
  readonly onFileSelected: (file: File) => void;
  readonly onReset?: () => void;
  readonly validationIssues?: readonly string[];
  readonly copy?: PhotoUploaderCopy;
  /** Outline drawn above the prompt. Defaults to the aesthetics face oval. */
  readonly frame?: (isDragging: boolean) => React.ReactNode;
};
export function AestheticsPhotoUploader(t0) {
  const $ = _c(119);
  const {
    disabled: t1,
    errorMessage,
    onFileSelected,
    onReset,
    validationIssues,
    copy: t2,
    frame
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const copy = t2 === undefined ? AESTHETICS_PHOTO_UPLOADER_COPY : t2;
  const {
    t
  } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [internalError, setInternalError] = useState(null);
  const fileInputRef = useRef(null);
  const resolvedError = errorMessage ?? internalError;
  const showError = Boolean(resolvedError);
  let t3;
  if ($[0] !== copy.dropPhoto || $[1] !== copy.tapToUpload || $[2] !== isDragging || $[3] !== t) {
    t3 = isDragging ? t(copy.dropPhoto) : t(copy.tapToUpload);
    $[0] = copy.dropPhoto;
    $[1] = copy.tapToUpload;
    $[2] = isDragging;
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const promptText = t3;
  let t4;
  if ($[5] !== copy.dropPhoto || $[6] !== copy.tapToUploadMobile || $[7] !== isDragging || $[8] !== t) {
    t4 = isDragging ? t(copy.dropPhoto) : t(copy.tapToUploadMobile);
    $[5] = copy.dropPhoto;
    $[6] = copy.tapToUploadMobile;
    $[7] = isDragging;
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const mobilePromptText = t4;
  let t5;
  let t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <User className={TIP_ICON_CLASS} key="subject" />;
    t6 = <Sun className={TIP_ICON_CLASS} key="lighting" />;
    t7 = <Glasses className={TIP_ICON_CLASS} key="accessories" />;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
  }
  let handleInputChange;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t8;
  let t9;
  if ($[13] !== copy.bestResults || $[14] !== copy.fileTooLarge || $[15] !== copy.hints || $[16] !== copy.supportedFormats || $[17] !== copy.supportedFormatsMobile || $[18] !== copy.tryDifferentPhoto || $[19] !== copy.unsupportedFileType || $[20] !== disabled || $[21] !== frame || $[22] !== isDragging || $[23] !== mobilePromptText || $[24] !== onFileSelected || $[25] !== onReset || $[26] !== promptText || $[27] !== resolvedError || $[28] !== showError || $[29] !== t || $[30] !== validationIssues) {
    const hintIcons = [t5, t6, t7];
    const guidanceHints = copy.hints.map((hintKey, index) => ({
      icon: hintIcons[index],
      text: t(hintKey)
    }));
    let t15;
    if ($[39] !== copy.fileTooLarge || $[40] !== copy.unsupportedFileType || $[41] !== t) {
      t15 = file => {
        if (!ACCEPTED_TYPES.includes(file.type) && !file.type.startsWith("image/")) {
          return t(copy.unsupportedFileType);
        }
        if (file.size > MAX_FILE_SIZE) {
          return t(copy.fileTooLarge);
        }
        return null;
      };
      $[39] = copy.fileTooLarge;
      $[40] = copy.unsupportedFileType;
      $[41] = t;
      $[42] = t15;
    } else {
      t15 = $[42];
    }
    const validateFile = t15;
    let t16;
    if ($[43] !== onFileSelected || $[44] !== validateFile) {
      t16 = file_0 => {
        setInternalError(null);
        const validationError = validateFile(file_0);
        if (validationError) {
          setInternalError(validationError);
          return;
        }
        onFileSelected(file_0);
      };
      $[43] = onFileSelected;
      $[44] = validateFile;
      $[45] = t16;
    } else {
      t16 = $[45];
    }
    const processFile = t16;
    let t17;
    if ($[46] !== disabled) {
      t17 = () => {
        if (!disabled) {
          fileInputRef.current?.click();
        }
      };
      $[46] = disabled;
      $[47] = t17;
    } else {
      t17 = $[47];
    }
    const openFilePicker = t17;
    let t18;
    if ($[48] !== processFile) {
      t18 = event => {
        const file_1 = event.target.files?.[0];
        if (file_1) {
          processFile(file_1);
        }
        event.target.value = "";
      };
      $[48] = processFile;
      $[49] = t18;
    } else {
      t18 = $[49];
    }
    handleInputChange = t18;
    let t19;
    if ($[50] !== disabled) {
      t19 = event_0 => {
        event_0.preventDefault();
        if (!disabled) {
          setIsDragging(true);
        }
      };
      $[50] = disabled;
      $[51] = t19;
    } else {
      t19 = $[51];
    }
    const handleDragOver = t19;
    let t20;
    if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = () => {
        setIsDragging(false);
      };
      $[52] = t20;
    } else {
      t20 = $[52];
    }
    const handleDragLeave = t20;
    let t21;
    if ($[53] !== disabled || $[54] !== processFile) {
      t21 = event_1 => {
        event_1.preventDefault();
        setIsDragging(false);
        if (disabled) {
          return;
        }
        const file_2 = event_1.dataTransfer.files[0];
        if (file_2) {
          processFile(file_2);
        }
      };
      $[53] = disabled;
      $[54] = processFile;
      $[55] = t21;
    } else {
      t21 = $[55];
    }
    const handleDrop = t21;
    t12 = "flex w-full flex-col gap-3 sm:gap-5";
    if ($[56] !== copy.tryDifferentPhoto || $[57] !== onReset || $[58] !== resolvedError || $[59] !== showError || $[60] !== t || $[61] !== validationIssues) {
      t13 = showError ? <div className="border-destructive/30 bg-destructive/10 shrink-0 rounded-2xl border px-4 py-3"><div className="flex items-start gap-3"><AlertCircle className="text-destructive mt-0.5 h-5 w-5 shrink-0" /><div className="min-w-0 flex-1"><Text component="p" text={resolvedError ?? ""} tone="white" variant="body-sm" weight="medium" />{validationIssues && validationIssues.length > 0 ? <ul className="mt-2 space-y-1">{validationIssues.map(_temp)}</ul> : null}</div></div>{onReset ? <button className="mt-3 min-h-11 underline-offset-2 hover:underline" type="button" onClick={() => {
          setInternalError(null);
          onReset();
        }}><Text component="span" text={t(copy.tryDifferentPhoto)} tone="white" variant="body-sm" weight="medium" /></button> : null}</div> : null;
      $[56] = copy.tryDifferentPhoto;
      $[57] = onReset;
      $[58] = resolvedError;
      $[59] = showError;
      $[60] = t;
      $[61] = validationIssues;
      $[62] = t13;
    } else {
      t13 = $[62];
    }
    const t22 = `mx-auto flex w-full max-w-sm cursor-pointer touch-manipulation flex-col items-center gap-2 text-center transition-opacity hover:opacity-95 active:opacity-90 sm:gap-3 ${disabled ? "pointer-events-none opacity-50" : ""}`;
    let t23;
    if ($[63] !== frame || $[64] !== isDragging) {
      t23 = frame ? frame(isDragging) : <FaceOutlineUploadFrame isDragging={isDragging} />;
      $[63] = frame;
      $[64] = isDragging;
      $[65] = t23;
    } else {
      t23 = $[65];
    }
    let t24;
    if ($[66] !== mobilePromptText) {
      t24 = <Text component="p" align="center" display="block" text={mobilePromptText} tone="white" variant="body-sm" weight="medium" />;
      $[66] = mobilePromptText;
      $[67] = t24;
    } else {
      t24 = $[67];
    }
    let t25;
    if ($[68] !== copy.supportedFormatsMobile || $[69] !== t) {
      t25 = t(copy.supportedFormatsMobile);
      $[68] = copy.supportedFormatsMobile;
      $[69] = t;
      $[70] = t25;
    } else {
      t25 = $[70];
    }
    let t26;
    if ($[71] !== t25) {
      t26 = <Text component="p" align="center" display="block" text={t25} tone="white" variant="caption" />;
      $[71] = t25;
      $[72] = t26;
    } else {
      t26 = $[72];
    }
    let t27;
    if ($[73] !== t24 || $[74] !== t26) {
      t27 = <div className="sm:hidden">{t24}{t26}</div>;
      $[73] = t24;
      $[74] = t26;
      $[75] = t27;
    } else {
      t27 = $[75];
    }
    let t28;
    if ($[76] !== promptText) {
      t28 = <Text component="p" align="center" display="block" text={promptText} tone="white" variant="body-sm" weight="medium" />;
      $[76] = promptText;
      $[77] = t28;
    } else {
      t28 = $[77];
    }
    let t29;
    if ($[78] !== copy.supportedFormats || $[79] !== t) {
      t29 = t(copy.supportedFormats);
      $[78] = copy.supportedFormats;
      $[79] = t;
      $[80] = t29;
    } else {
      t29 = $[80];
    }
    let t30;
    if ($[81] !== t29) {
      t30 = <Text component="p" align="center" display="block" text={t29} tone="white" variant="caption" />;
      $[81] = t29;
      $[82] = t30;
    } else {
      t30 = $[82];
    }
    let t31;
    if ($[83] !== t28 || $[84] !== t30) {
      t31 = <div className="hidden space-y-1 sm:block">{t28}{t30}</div>;
      $[83] = t28;
      $[84] = t30;
      $[85] = t31;
    } else {
      t31 = $[85];
    }
    let t32;
    if ($[86] !== t27 || $[87] !== t31) {
      t32 = <div className="w-full space-y-1 px-2">{t27}{t31}</div>;
      $[86] = t27;
      $[87] = t31;
      $[88] = t32;
    } else {
      t32 = $[88];
    }
    if ($[89] !== disabled || $[90] !== handleDragOver || $[91] !== handleDrop || $[92] !== openFilePicker || $[93] !== t22 || $[94] !== t23 || $[95] !== t32) {
      t14 = <button className={t22} disabled={disabled} type="button" onClick={openFilePicker} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop}>{t23}{t32}</button>;
      $[89] = disabled;
      $[90] = handleDragOver;
      $[91] = handleDrop;
      $[92] = openFilePicker;
      $[93] = t22;
      $[94] = t23;
      $[95] = t32;
      $[96] = t14;
    } else {
      t14 = $[96];
    }
    t10 = "w-full shrink-0 pb-1";
    let t33;
    if ($[97] !== copy.bestResults || $[98] !== t) {
      t33 = t(copy.bestResults);
      $[97] = copy.bestResults;
      $[98] = t;
      $[99] = t33;
    } else {
      t33 = $[99];
    }
    if ($[100] !== t33) {
      t11 = <Text component="p" text={t33} tone="white" variant="overline" />;
      $[100] = t33;
      $[101] = t11;
    } else {
      t11 = $[101];
    }
    t8 = "mt-2 space-y-1.5 sm:mt-3 sm:space-y-2";
    t9 = guidanceHints.map(_temp2);
    $[13] = copy.bestResults;
    $[14] = copy.fileTooLarge;
    $[15] = copy.hints;
    $[16] = copy.supportedFormats;
    $[17] = copy.supportedFormatsMobile;
    $[18] = copy.tryDifferentPhoto;
    $[19] = copy.unsupportedFileType;
    $[20] = disabled;
    $[21] = frame;
    $[22] = isDragging;
    $[23] = mobilePromptText;
    $[24] = onFileSelected;
    $[25] = onReset;
    $[26] = promptText;
    $[27] = resolvedError;
    $[28] = showError;
    $[29] = t;
    $[30] = validationIssues;
    $[31] = handleInputChange;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
    $[37] = t8;
    $[38] = t9;
  } else {
    handleInputChange = $[31];
    t10 = $[32];
    t11 = $[33];
    t12 = $[34];
    t13 = $[35];
    t14 = $[36];
    t8 = $[37];
    t9 = $[38];
  }
  let t15;
  if ($[102] !== t8 || $[103] !== t9) {
    t15 = <div className={t8}>{t9}</div>;
    $[102] = t8;
    $[103] = t9;
    $[104] = t15;
  } else {
    t15 = $[104];
  }
  let t16;
  if ($[105] !== t10 || $[106] !== t11 || $[107] !== t15) {
    t16 = <div className={t10}>{t11}{t15}</div>;
    $[105] = t10;
    $[106] = t11;
    $[107] = t15;
    $[108] = t16;
  } else {
    t16 = $[108];
  }
  let t17;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = ACCEPTED_TYPES.join(",");
    $[109] = t17;
  } else {
    t17 = $[109];
  }
  let t18;
  if ($[110] !== disabled || $[111] !== handleInputChange) {
    t18 = <input accept={t17} className="hidden" data-testid="aesthetics-photo-uploader-input" disabled={disabled} ref={fileInputRef} type="file" onChange={handleInputChange} />;
    $[110] = disabled;
    $[111] = handleInputChange;
    $[112] = t18;
  } else {
    t18 = $[112];
  }
  let t19;
  if ($[113] !== t12 || $[114] !== t13 || $[115] !== t14 || $[116] !== t16 || $[117] !== t18) {
    t19 = <div className={t12}>{t13}{t14}{t16}{t18}</div>;
    $[113] = t12;
    $[114] = t13;
    $[115] = t14;
    $[116] = t16;
    $[117] = t18;
    $[118] = t19;
  } else {
    t19 = $[118];
  }
  return t19;
}
function _temp2(hint) {
  return <div className="rounded-xl border border-white/10 bg-white/5 px-2.5 py-2.5 sm:px-3 sm:py-3" key={hint.text}><div className="type-body-sm flex items-center gap-2.5 text-white sm:gap-3"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/8 sm:h-10 sm:w-10">{hint.icon}</span><span className="min-w-0 text-left">{hint.text}</span></div></div>;
}
function _temp(issue) {
  return <li key={issue}><Text component="span" text={issue} tone="white" variant="caption" /></li>;
}
type FaceOutlineUploadFrameProps = {
  readonly children?: React.ReactNode;
  readonly isDragging: boolean;
};
export function FaceOutlineUploadFrame(t0) {
  const $ = _c(13);
  const {
    children,
    isDragging
  } = t0;
  const outlineBorderClass = isDragging ? "border-primary/70" : "border-white/40";
  const outlineGlowClass = isDragging ? "shadow-[0_0_24px_hsl(var(--primary)/0.35)]" : "";
  const t1 = `absolute inset-0 rounded-[50%] border-2 bg-white/6 transition-colors ${outlineBorderClass} ${outlineGlowClass}`;
  let t2;
  if ($[0] !== t1) {
    t2 = <div className={t1} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["radial-gradient(circle at 0% 0%, rgba(255,255,255,0.14) 0%, transparent 48%)", "radial-gradient(circle at 100% 0%, rgba(255,255,255,0.14) 0%, transparent 48%)", "radial-gradient(circle at 0% 100%, rgba(255,255,255,0.14) 0%, transparent 48%)", "radial-gradient(circle at 100% 100%, rgba(255,255,255,0.14) 0%, transparent 48%)"];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  let t5;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="pointer-events-none absolute inset-0 rounded-[50%]" style={{
      background: t3.join(", ")
    }} />;
    t5 = <div className="absolute top-[38%] right-4 left-4 h-px bg-white/25" />;
    t6 = <div className="absolute top-8 bottom-8 left-1/2 w-px -translate-x-1/2 bg-white/15" />;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t4 = $[3];
    t5 = $[4];
    t6 = $[5];
  }
  let t10;
  let t7;
  let t8;
  let t9;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <FaceOutlineCorner className="-top-2 -left-2 rounded-tl-lg border-t-2 border-l-2" />;
    t8 = <FaceOutlineCorner className="-top-2 -right-2 rounded-tr-lg border-t-2 border-r-2" />;
    t9 = <FaceOutlineCorner className="-bottom-2 -left-2 rounded-bl-lg border-b-2 border-l-2" />;
    t10 = <FaceOutlineCorner className="-right-2 -bottom-2 rounded-br-lg border-r-2 border-b-2" />;
    $[6] = t10;
    $[7] = t7;
    $[8] = t8;
    $[9] = t9;
  } else {
    t10 = $[6];
    t7 = $[7];
    t8 = $[8];
    t9 = $[9];
  }
  let t11;
  if ($[10] !== children || $[11] !== t2) {
    t11 = <div aria-hidden={true} className={FACE_OUTLINE_FRAME_CLASS}>{t2}{t4}{t5}{t6}{children}{t7}{t8}{t9}{t10}</div>;
    $[10] = children;
    $[11] = t2;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  return t11;
}
type FaceOutlineCornerProps = {
  readonly className: string;
};
function FaceOutlineCorner(t0) {
  const $ = _c(2);
  const {
    className
  } = t0;
  const t1 = `absolute h-6 w-6 border-white/55 ${className}`;
  let t2;
  if ($[0] !== t1) {
    t2 = <div className={t1} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}